import { Typography, Icon } from "@material-ui/core";
import { formatDate } from "app/main/utils/dateUtils";
import { formatHosDuration, getHosDisplayConfig } from "app/main/utils/uiUtils";
import HosCombinedUsageBar from "./HosCombinedUsageBar";

const HOS_DRIVE_WARNING_SECONDS = 1800;
const HOS_DUTY_AT_STOP_DEFAULT_SECONDS = 3 * 3600;

/**
 * Rich tooltip content for the merged Alert column (arrival + HOS for next stop).
 */
function TripAlertTooltip({ stopArrivalStatus, currentETA, currentETD, hosStatus, hosEstimate }) {
	const hasArrival = stopArrivalStatus && stopArrivalStatus !== "N/A";
	const hasHos = hosStatus || hosEstimate;

	if (!hasArrival && !hasHos) return null;

	const secondsToStop = hosEstimate?.secondsToStop;
	const driveRemaining = hosEstimate?.driveRemaining;
	const dutyRemaining = hosEstimate?.dutyRemaining;
	const dutyRequiredAtStop = hosEstimate?.dutyRequiredAtStop ?? HOS_DUTY_AT_STOP_DEFAULT_SECONDS;

	const driveWarning =
		hosStatus === "Warning" &&
		(typeof driveRemaining === "number" ? driveRemaining < HOS_DRIVE_WARNING_SECONDS : false);
	const dutyWarning =
		hosStatus === "Warning" && (typeof dutyRemaining === "number" ? dutyRemaining < dutyRequiredAtStop : false);

	const hosDisplay = getHosDisplayConfig(hosStatus);

	return (
		<div className="p-8 min-w-256 max-w-320">
			{(hasArrival || hasHos) && (
				<div className="flex flex-row justify-between mb-8">
					{hasArrival && (
						<Typography className="text-12 font-semibold" component="div">
							Arrival: {stopArrivalStatus}
						</Typography>
					)}
					{hasHos && hosDisplay && (
						<Typography
							className={`text-12 font-semibold flex flex-row items-center ${hosDisplay.color}`}
							component="div"
						>
							<Icon className={`text-14 mr-4 ${hosDisplay.iconClass}`}>{hosDisplay.icon}</Icon>
							{hosDisplay.label}
						</Typography>
					)}
				</div>
			)}

			{hasArrival && (
				<div className="mb-8">
					{currentETA && (
						<Typography className="text-11" component="div">
							ETA: {formatDate(currentETA)}
						</Typography>
					)}
					{currentETD && (
						<Typography className="text-11" component="div">
							ETD: {formatDate(currentETD)}
							<Typography className="text-10 text-grey-500 ml-4" component="span">
								(ETA + 3h dwell)
							</Typography>
						</Typography>
					)}
				</div>
			)}

			{hasHos && (
				<div className="flex flex-col space-y-8">
					{secondsToStop != null && driveRemaining != null && dutyRemaining != null ? (
						<HosCombinedUsageBar
							secondsToStop={secondsToStop}
							driveRemaining={driveRemaining}
							dutyRemaining={dutyRemaining}
							dutyRequiredAtStop={dutyRequiredAtStop}
							driveWarning={driveWarning || driveRemaining < HOS_DRIVE_WARNING_SECONDS}
							dutyWarning={dutyWarning || dutyRemaining < dutyRequiredAtStop}
						/>
					) : (
						<>
							{secondsToStop != null && (
								<Typography className="text-11 font-medium" component="div">
									Drive to stop: {formatHosDuration(secondsToStop)}
								</Typography>
							)}
							{driveRemaining != null && (
								<Typography className="text-11" component="div">
									Drive remaining: {formatHosDuration(driveRemaining)}
								</Typography>
							)}
							{dutyRemaining != null && (
								<Typography className="text-11" component="div">
									Duty remaining: {formatHosDuration(dutyRemaining)} ({formatHosDuration(dutyRequiredAtStop)} required
									at stop)
								</Typography>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
}

export default TripAlertTooltip;
